import React, { useMemo } from "react";
import { SERVICE_SEO } from "../data/seo";
import { Link, useParams } from "react-router-dom";
import { CheckCircle, CaretLeft } from "@phosphor-icons/react";
import SEO, { breadcrumbJsonLd } from "../components/SEO";
import { findService, SERVICES, PACKAGE_ONLY_SLUGS } from "../data/services";

export default function ServiceDetail() {
  const { slug } = useParams();
  const s = findService(slug);
  const jsonLd = useMemo(() => (s ? {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "MedicalProcedure",
        name: s.title,
        alternateName: SERVICE_SEO[s.slug]?.aka,
        description: s.description,
        provider: { "@id": "https://camcpc.co.uk/#clinic" },
        areaServed: "Cambridge, UK",
        procedureType: "https://schema.org/DiagnosticProcedure",
      },
      breadcrumbJsonLd([
        { name: "Services", path: "/services" },
        { name: s.title, path: `/services/${s.slug}` },
      ]),
    ],
  } : null), [s]);
  if (!s) {
    return (
      <div className="container-ccc py-24">
        <p>Service not found.</p>
        <Link to="/services" className="text-bronze-dark">← Back to services</Link>
      </div>
    );
  }
  const others = SERVICES.filter((x) => x.slug !== slug).slice(0, 3);
  return (
    <div data-testid={`service-detail-${s.slug}`}>
      <SEO
        title={SERVICE_SEO[s.slug]?.title || s.title}
        description={SERVICE_SEO[s.slug]?.description || s.summary}
        path={`/services/${s.slug}`}
        jsonLd={jsonLd}
      />
      <section className="bg-cream-100 border-b border-bronze-light/30">
        <div className="container-ccc py-16">
          <Link to="/services" className="inline-flex items-center gap-1 text-ink-muted hover:text-bronze-dark text-sm"><CaretLeft size={14}/> All services</Link>
          <div className="eyebrow mt-8">Service</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-3 leading-[1.02] max-w-3xl">{s.title}</h1>
          <p className="mt-6 max-w-2xl text-ink-muted text-lg leading-relaxed">{s.summary}</p>
        </div>
      </section>

      <section className="container-ccc py-20 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8 space-y-5 text-ink-muted text-base leading-relaxed whitespace-pre-line">
          {s.description}
        </div>
        <aside className="lg:col-span-4 p-8 bg-white border border-stone-200/60 rounded-md h-fit">
          <div className="eyebrow">What's included</div>
          <ul className="mt-5 space-y-3">
            {s.bullets.map((b) => (
              <li key={b} className="flex gap-3 items-start text-sm text-ink">
                <CheckCircle size={20} className="text-bronze-dark mt-0.5 shrink-0" weight="duotone" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
          {PACKAGE_ONLY_SLUGS.includes(s.slug) ? (<>
            <p data-testid="service-package-note" className="mt-8 text-sm text-ink-muted">This study is offered as part of a separately priced package of vascular studies, not with the standard consultation.</p>
            <Link data-testid="service-detail-enquiry-cta" to="/contact" className="btn-primary w-full mt-4">Ask about packages</Link>
          </>) : (
            <Link data-testid="service-detail-enquiry-cta" to="/enquiry" className="btn-primary w-full mt-8">Request consultation</Link>
          )}
        </aside>
      </section>

      <section className="bg-cream-100 border-t border-bronze-light/30">
        <div className="container-ccc py-16">
          <div className="eyebrow">Related services</div>
          <div className="mt-6 grid md:grid-cols-3 gap-6">
            {others.map((o) => (
              <Link key={o.slug} to={`/services/${o.slug}`} className="block p-6 bg-white border border-stone-200/60 rounded-md hover:-translate-y-1 hover:shadow-md transition-all">
                <div className="font-serif text-xl">{o.title}</div>
                <p className="mt-2 text-sm text-ink-muted">{o.summary}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
